import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { XtItemsTab } from '../merchants/xt-items-tab';

// Top-level XT Items page. Reads the XtSyncDb replica's item table, per merchant, via the shared
// XtItemsTab component — the same component the merchant detail page mounts on its XT Items tab, so the
// two surfaces cannot drift into disagreeing about what a row says.
//
// This page answers a different question from the tab: "show me any merchant's XT items without
// navigating to that merchant". Hence the picker, which the tab does not have — there the merchant is
// already decided by the page you are on.
//
// NOT the Products page. Products is the platform's event-sourced catalog: what the middleware OBSERVED
// from XT and projected. This is the replicated XT table itself, unprojected, which is exactly what you
// want open when the two disagree — it answers "what does XT actually say" rather than "what did we
// record it saying".
//
// A MERCHANT MUST BE PICKED FIRST, and until one is, XtItemsTab is not mounted at all — so no request is
// issued, not even an empty one. That is not politeness about spinners: the item table holds every
// location's catalogue in one heap (plausibly hundreds of thousands of rows), the location is part of the
// API path rather than an optional filter, and there is deliberately no way to spell "all of them".
//
// The picker is the platform's onboarded merchants (getLocations() — the same /central/admin/locations the
// Merchants page reads), NOT the distinct location ids in the item table. The replica may well hold
// locations this platform never onboarded; they are not merchants here, and the server refuses them.
@Component({
    selector: 'app-xt-items',
    standalone: true,
    imports: [CardModule, SelectModule, MessageModule, ButtonModule, TooltipModule, FormsModule, RouterLink, XtItemsTab],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>XT Items</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Items as the POS holds them. The online catalog is under Products." tooltipPosition="top"></i></ng-template>
            <!-- THE TWO ITEM SURFACES ARE DIFFERENT THINGS AND OPERATORS CONFLATE THEM. Said on both pages
                 rather than only on the newer one — whichever you landed on, you need to know which side of
                 the comparison you are standing on. -->

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchantOptions()" [(ngModel)]="locationId" (onChange)="onSelect()"
                          placeholder="Select a merchant" [filter]="true" styleClass="w-72" />

                <!-- The way out of the picker and into the location, where the projection sits one tab
                     away. Only offered once a merchant is chosen — there is nothing to open before that. -->
                @if (activeLocation(); as loc) {
                    <p-button icon="pi pi-external-link" label="Open location" size="small" [text]="true"
                              [routerLink]="['/locations', loc]" />
                }
            </div>

            @if (pickerError(); as e) {
                <p-message severity="error" styleClass="mb-3">{{ e }}</p-message>
            }

            @if (activeLocation(); as loc) {
                <app-xt-items-tab [locationId]="loc" />
            } @else {
                <p class="text-surface-500 dark:text-surface-400">
                    Select a merchant to browse its XT items.
                </p>
            }
        </p-card>
    `
})
export class XtItems implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);

    /** The merchant the picker is showing. null until one is chosen — and nothing is fetched before that. */
    locationId: string | null = null;

    /** The location actually mounted into XtItemsTab. Kept separate from the picker's model so a merchant
     *  switch can remount the tab (its [lazy] table re-fires onLazyLoad on render — the input change alone
     *  would not reload), and so the tab is created only once a merchant exists: an un-mounted [lazy] table
     *  cannot fire onLazyLoad, which is what makes "no merchant, no query" structural rather than a guard
     *  someone can delete. */
    readonly activeLocation = signal<string | null>(null);
    readonly merchantOptions = signal<{ label: string; value: string }[]>([]);
    readonly pickerError = signal<string | null>(null);

    ngOnInit(): void {
        // Deep-link support, the same shape the Products page uses: /xt-items?locationId=X.
        const fromQuery = this.route.snapshot.queryParamMap.get('locationId');
        this.locationId = fromQuery;
        this.activeLocation.set(fromQuery);

        // Merchants = ONBOARDED locations. Reused as-is from /central/admin/locations rather than derived
        // from DISTINCT locationid in the item table: the replica may carry locations the platform has not
        // onboarded, and those are not merchants. Deliberately NOT narrowed to the enabled Magento
        // instance either — XT replicates a location's items whether or not its Magento instance happens
        // to be toggled on, so filtering on that would hide a merchant's item master because of a setting
        // on another page.
        this.api.getLocations().subscribe({
            next: (r) => this.merchantOptions.set(
                (r.locations ?? []).map((l) => ({
                    // Label by merchant code, falling through to the store code and name, so the picker
                    // never regresses to a bare guid. The VALUE stays the location id — what XT keys on.
                    label: l.merchantCode || l.magentoCode || l.name || l.locationId,
                    value: l.locationId
                }))
            ),
            error: (e) => this.pickerError.set(this.describe(e))
        });
    }

    onSelect(): void {
        // Remount XtItemsTab so its lazy table re-fires onLazyLoad and loads the new merchant. Unmounting
        // first also drops the old rows immediately, rather than leaving one merchant's catalogue on
        // screen under another's name while the request flies.
        this.activeLocation.set(null);
        setTimeout(() => this.activeLocation.set(this.locationId));
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        const server = e?.error?.error ?? e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
